import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, CircularProgress, alpha, useTheme,
} from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import SettingsIcon from '@mui/icons-material/Settings';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';

const STRATEGY_ENGINE_URL =
  process.env.REACT_APP_STRATEGY_ENGINE_URL ||
  'https://strategy-engine.houman-ghavamzadeh.workers.dev';

export default function AuditLogTab({ strategyId }) {
  const theme = useTheme();
  const [auditLog, setAuditLog] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadAuditLog = async () => {
      if (!strategyId) return;
      setLoading(true);
      try {
        const res = await fetch(`${STRATEGY_ENGINE_URL}/api/strategies/${strategyId}/versions`, {
          headers: { 'X-User-Id': localStorage.getItem('user_id') || 'default-user' },
        });
        const data = await res.json();
        const versions = data.data || data || [];
        setAuditLog(Array.isArray(versions) ? versions : []);
      } catch (err) {
        console.error('Failed to load audit log:', err);
        setAuditLog([]);
      } finally {
        setLoading(false);
      }
    };
    loadAuditLog();
  }, [strategyId]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (auditLog.length === 0) {
    return (
      <Paper elevation={0} sx={{ p: 4, border: '1px solid', borderColor: 'divider', borderRadius: 2, textAlign: 'center' }}>
        <HistoryIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
        <Typography variant="body2" color="text.secondary">No audit log entries yet.</Typography>
        <Typography variant="caption" color="text.secondary">Changes to strategy settings will appear here.</Typography>
      </Paper>
    );
  }

  return (
    <Box>
      <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Time</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Action</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Changes</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Version</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {auditLog.slice(0, 50).map((entry, i) => {
              const timestamp = entry.createdAt || entry.timestamp || entry.savedAt;
              const changes = entry.changeSummary || entry.changes || entry.diff;
              const version = entry.version || entry.versionNumber || i + 1;

              return (
                <TableRow key={i} hover>
                  <TableCell>
                    <Typography variant="caption">
                      {timestamp ? new Date(timestamp).toLocaleString() : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <SettingsIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                      <Typography variant="caption">
                        {entry.action || entry.type || 'Settings Update'}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    {typeof changes === 'string' ? (
                      <Typography variant="caption" color="text.secondary">{changes.substring(0, 100)}</Typography>
                    ) : changes && typeof changes === 'object' ? (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {Object.entries(changes).slice(0, 5).map(([key, val]) => (
                          <Chip
                            key={key}
                            label={`${key}: ${typeof val === 'object' ? `${val.from}→${val.to}` : String(val).substring(0, 20)}`}
                            size="small"
                            variant="outlined"
                            icon={<SwapHorizIcon sx={{ fontSize: '12px !important' }} />}
                            sx={{ fontSize: '0.6rem', height: 20 }}
                          />
                        ))}
                        {Object.keys(changes).length > 5 && (
                          <Chip label={`+${Object.keys(changes).length - 5} more`} size="small" sx={{ fontSize: '0.6rem', height: 20 }} />
                        )}
                      </Box>
                    ) : (
                      <Typography variant="caption" color="text.secondary">—</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip label={`v${version}`} size="small" variant="outlined" sx={{ fontSize: '0.6rem', height: 20 }} />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
